import React from 'react'
import * as CopyToClipboard from 'react-copy-to-clipboard'
import { camelCase, snakeCase } from 'change-case'
import autobind from 'autobind-decorator'
import { Container } from 'typescript-ioc/es5'
import {
    CloudDownloadOutlined,
    LoadingOutlined,
    ReloadOutlined,
    SaveOutlined,
    ShareAltOutlined,
    StarFilled,
    StarOutlined,
} from '@ant-design/icons'
import { IValueDidChange, observe } from 'mobx'
import { AppState } from '../../../stores/app'
import { AppService } from '../../../services'

import { BetterComponent } from '../../../components/better-component/better-component'
import { arrayContains, simpleNotification } from '../../../_utils/utils'
import { InsightsService } from '../../../services/insights'
import { DomainDto } from '../../../dtos/domain'
import { SHORT_DATE_FORMAT } from '../../../constants'
import {
    TIME_RANGE_PRESETS,
    TimeRangePresetKey,
    TR_PRESET_ALL_TIME,
    TR_PRESET_LAST_7_DAYS,
} from '../constants/time-range-options'
import { DATE_INCREMENT_DROPDOWN_OPTIONS } from '../constants/date-increment-dropdown-options'
import { DEFAULT_TYPE, TYPE_DROPDOWN_OPTIONS } from '../constants/type-dropdown-options'
import { DEFAULT_LEVEL, LEVEL_DROPDOWN_OPTIONS } from '../constants/level-dropdown-options'
import { BREAKDOWN_DROPDOWN_OPTIONS, DEFAULT_BREAKDOWN } from '../constants/breakdown-dropdown-options'
import { COLUMN_DROPDOWN_OPTIONS } from '../constants/column-dropdown-options'
import { FILTER_DROPDOWN_OPTIONS } from '../constants/filter-dropdown-options'
import { IReportConfig } from '../interfaces/report-config'
import { IReportStatus } from '../interfaces/report-status'
import { ReportStatusCode } from '../enums/report-status-code'
import { VALUELESS_OPERATORS } from '../enums/report-filter-operator'
import clone from 'clone'
import type { RangeValueWithPreset } from '../../../types/range-value'
import {
    buildApiReportContract,
    InsightsReportContract,
    ReportContractOptions,
    ReportFilter,
} from '../helpers/build-api-report-contract'
import {
    BreakdownDropdownOption,
    ColumnDropdownOption,
    EntityConditionOptions,
    FiltersDropdownOption,
    InsightsDropdownGroup,
    LevelDropdownOption,
    TypeDropdownOption,
} from '../types'
import { GetAccountResponse } from '@pushly/models/lib/structs'
import { ReportConfigurationLevel } from '../enums/report-configuration-level'
import { ReportConfigurationType } from '../enums/report-configuration-type'
import { ReportConfigurationBreakdown } from '../enums/report-configuration-breakdown'
import { PlatformLevel } from '../enums/platform-level'
import { ReportDateIncrement } from '../enums/report-date-increment'
import { Button as AntDButton, DatePicker, Form, Input, Popover, Space, Tooltip, Flex, Divider } from 'antd'
import { isDeliveryTestNotificationAlias, resolveEffectiveLevelForCatalog } from '../constants/column-table-order'
import dayjs, { Dayjs } from 'dayjs'
import { Button, Select, FilterBar } from '@pushly/aqe/lib/components'
import type { ActiveFilter, FilterDefinition } from '@pushly/aqe/lib/components'
import { toReportFilters, fromReportFilters } from '../helpers/filter-bar-converters'
import { buildReportingFilterDefinitions } from '../helpers/reporting-filter-definitions'

import './report-builder.scss'

const FILTERS_ENABLED: boolean = true

let UPDATE_DEBOUNCE: any

interface IShareReportProps {
    reportConfig: IReportConfig
    visible: boolean
    onClose?: () => void
}

interface IShareReportState {
    generatedReport?: any
}

export class ShareReportPanel extends BetterComponent<IShareReportProps, IShareReportState> {
    private appState: AppState
    private insightsService: InsightsService

    private textboxRef: any

    public constructor(props: IShareReportProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.insightsService = Container.get(InsightsService)

        this.state = {}
    }

    public async componentDidMount(): Promise<void> {
        if (this.props.visible) {
            const globalTable = (window as any).__SW_REPORT_TABLE__ || {}
            const reportConfig = {
                ...this.props.reportConfig,
                tableSort: {
                    sortBy: globalTable.sortBy,
                    sortDirection: globalTable.sortDirection,
                },
            }

            const generatedReport = await this.insightsService.generateSharedReport(
                this.appState.currentDomain!.id,
                reportConfig,
                false,
                'generateSharedReport',
            )

            if (generatedReport) {
                this.setState({ generatedReport })

                if (!!this.textboxRef && !!this.textboxRef.textAreaRef) {
                    this.textboxRef.textAreaRef.select()
                }
            }
        }
    }

    public render(): React.ReactNode {
        const { generatedReport } = this.state

        let reportLink = ''
        if (generatedReport) {
            reportLink = `${this.appState.platformHost}/domains/${this.appState.currentDomain!.id}/insights?report_id=${
                generatedReport.id
            }`
        }

        return !generatedReport ? (
            <>
                <div className="loading-link-wrapper">
                    <LoadingOutlined spin={true} />
                    <span>Loading report link...</span>
                </div>
            </>
        ) : (
            <Flex vertical gap={8}>
                <Input.TextArea
                    ref={(textbox) => (this.textboxRef = textbox)}
                    className="copy-disabled"
                    readOnly={true}
                    value={reportLink}
                    style={{
                        resize: 'none',
                    }}
                />

                <Space align="end">
                    <AntDButton size="small" type="default" onClick={this.handleClose}>
                        <span>Cancel</span>
                    </AntDButton>
                    <CopyToClipboard
                        onCopy={() => {
                            simpleNotification('success', 'Report link has been copied.')
                            this.handleClose()
                        }}
                        text={reportLink}
                    >
                        <AntDButton type="primary" size="small" onClick={() => {}}>
                            <span>Copy Report Link</span>
                        </AntDButton>
                    </CopyToClipboard>
                </Space>
            </Flex>
        )
    }

    @autobind
    private async handleClose(): Promise<void> {
        if (!!this.props.onClose) {
            this.props.onClose()
        }
    }
}

export type ReportBuilderProps = {
    onStatusUpdate: (status: IReportStatus) => any
    onInsightsUpdate: (insights: any, config: IReportConfig, contract: any) => any
    level: PlatformLevel
    domain: DomainDto
    org?: GetAccountResponse
}

type ReportBuilderState = {
    platformLevel: PlatformLevel.ORG | PlatformLevel.DOMAIN
    currentDomain: DomainDto
    currentOrg?: GetAccountResponse
    savedReports: any[]
    currentSavedReport?: any
    reportConfig: IReportConfig
    showFilters: boolean
    lastCallSignature?: string
    loading?: boolean

    savePanelVisible: boolean
    sharePanelVisible: boolean

    updateKey?: string
    updating?: boolean
}

export class ReportBuilder extends BetterComponent<ReportBuilderProps, ReportBuilderState> {
    private appState: AppState
    private appService: AppService
    private insightsService: InsightsService

    private disposeObservers: any

    public constructor(props: ReportBuilderProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.insightsService = Container.get(InsightsService)

        const defaultType = this.determineDefaultTypeValue()
        const defaultLevel = this.determineDefaultLevelValue(defaultType)
        const defaultBreakdown = this.determineDefaultBreakdownValue(defaultLevel)

        this.state = {
            platformLevel: props.level,
            currentDomain: props.domain,
            currentOrg: props.org,
            showFilters: false,
            savedReports: [],
            reportConfig: {
                dateIncrement: ReportDateIncrement.DAY,
                dateRange: this.getDefaultReportDateRange(),
                type: defaultType,
                level: defaultLevel ?? ReportConfigurationLevel.DOMAIN,
                breakdown: defaultBreakdown ?? ReportConfigurationBreakdown.NONE,
                columns: [],
            },
            savePanelVisible: false,
            sharePanelVisible: false,
        }
    }

    public async componentDidMount(): Promise<void> {
        // Initialize default columns based on current config and trigger initial data fetch
        const { reportConfig } = this.state
        if (reportConfig.type && reportConfig.level && reportConfig.columns.length === 0) {
            const columnOptions = this.determineColumnOptions(
                reportConfig.type,
                reportConfig.level,
                reportConfig.breakdown,
            )
            const defaultColumns = this.determineDefaultColumnValues(columnOptions)
            if (defaultColumns.length > 0) {
                await this.setState(({ reportConfig: config }) => ({
                    reportConfig: {
                        ...config,
                        columns: defaultColumns,
                    },
                }))
                // Trigger initial data fetch now that columns are set
                this.handleConfigChange()
            }
        }

        this.readReportFromQueryString()

        this.disposeObservers = []

        if (this.state.platformLevel === PlatformLevel.DOMAIN) {
            this.disposeObservers.push(observe(this.appState, 'currentDomainJsonData', () => this.setDomainState()))

            this.fetchSavedReports()
        } else {
            this.disposeObservers.push(
                observe(this.appState, 'currentDomainJsonData', (change: IValueDidChange<string>) => {
                    const prev = DomainDto.fromApiResponse(JSON.parse(change.oldValue ?? '{}'))
                    const next = DomainDto.fromApiResponse(JSON.parse(change.newValue ?? '{}'))

                    if (!next || next.accountId === prev.accountId) return

                    this.appService.route(`organizations/${next.accountId}/notifications`)
                }),
            )
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount()

        this.disposeObservers.forEach((f: any) => f())
    }

    public render() {
        const { reportConfig, savedReports, currentSavedReport } = this.state

        const type = this.getCurrentType()
        const level = this.getCurrentLevel()
        const breakdown = this.getCurrentBreakdown()

        const levelKey = `${type}-LVL`
        const breakdownKey = `${type}_${level}-BRK`
        const columnsKey = `${type}_${level}-COL`

        const typeOptions = this.determineTypeOptions()
        const levelOptions = this.determineLevelOptions()
        const defaultLevelOption = this.determineDefaultLevelValue()
        const breakdownOptions = this.determineBreakdownOptions()
        const defaultBreakdownOption = this.determineDefaultBreakdownValue()
        const columnOptions = this.determineColumnOptions()
        const defaultColumnOptions = this.determineDefaultColumnValues(columnOptions)

        const filters = this.state.reportConfig.filters || []

        const actionsEnabled =
            !!reportConfig.type && !!reportConfig.level && !!reportConfig.breakdown && !!reportConfig.columns

        const since = reportConfig.dateRange?.since ? dayjs(reportConfig.dateRange?.since) : null
        const through = reportConfig.dateRange?.through ? dayjs(reportConfig.dateRange?.through) : null

        return (
            <Flex
                vertical
                gap={8}
                // key by domain ensures column re-rendering
                // occurs to account for conditions
                key={this.currentCtxEntity.id + '_' + this.currentCtxEntity.name}
                className="report-builder"
            >
                <Flex vertical>
                    <Flex justify="space-between">
                        <Select
                            className="report-type-header"
                            prefix="Report:"
                            aria-label="Report type"
                            placeholder="Choose your report"
                            showSearch={true}
                            onChange={this.setReportTypeState}
                            options={typeOptions}
                            value={!!currentSavedReport ? currentSavedReport.id : reportConfig.type}
                            style={{ width: 280 }}
                        />
                        <Space className="fx-col-group content-right">
                            <DatePicker.RangePicker
                                className="calendar-header"
                                dropdownClassName="reporting-date-range"
                                disabled={reportConfig.type === 'MONTHLY_UNIQUES'}
                                getPopupContainer={this.appService.getAppContainer}
                                format={SHORT_DATE_FORMAT}
                                presets={
                                    reportConfig.type === 'MONTHLY_UNIQUES' ? [TR_PRESET_ALL_TIME] : TIME_RANGE_PRESETS
                                }
                                defaultValue={this.defaultReportTimeRange}
                                value={[since, through]}
                                allowEmpty={true}
                                onCalendarChange={this.setDateRangeState}
                            />
                            <Select
                                className="report-date-increment"
                                prefix="Date Grouping:"
                                aria-label="Date grouping"
                                disabled={reportConfig.type === 'MONTHLY_UNIQUES'}
                                showSearch={false}
                                onChange={this.setDateIncrementState}
                                options={DATE_INCREMENT_DROPDOWN_OPTIONS[type || 'NONE']}
                                value={reportConfig.dateIncrement}
                                style={{ width: 180 }}
                            />
                        </Space>
                    </Flex>

                    <Flex justify="space-between" className="report-builder-lower">
                        <Space>
                            <Select
                                key={levelKey}
                                className="report-level-select"
                                style={{ width: 180 }}
                                size="small"
                                prefix="Level:"
                                aria-label="Report level"
                                placeholder="Select a level"
                                loading={!reportConfig.type}
                                disabled={reportConfig.type === 'MONTHLY_UNIQUES'}
                                showSearch={false}
                                onChange={this.setReportLevelState}
                                options={levelOptions}
                                defaultValue={defaultLevelOption ? defaultLevelOption : undefined}
                                value={reportConfig.level}
                            />
                            <Select
                                key={breakdownKey}
                                className="report-breakdown-select"
                                style={{ width: 240 }}
                                size="small"
                                prefix="Breakdown:"
                                aria-label="Report breakdown"
                                loading={!reportConfig.level}
                                disabled={reportConfig.level === 'TEST'}
                                showSearch={false}
                                onChange={this.setReportBreakdownState}
                                options={breakdownOptions}
                                defaultValue={defaultBreakdownOption ? defaultBreakdownOption : undefined}
                                value={reportConfig.breakdown}
                            />
                            <Select
                                key={columnsKey}
                                className="report-columns-select"
                                popupClassName="report-columns-select-dropdown"
                                style={{ width: 200 }}
                                size="small"
                                prefix="Columns:"
                                aria-label="Report columns"
                                mode="multiple"
                                loading={!reportConfig.level}
                                showSearch={false}
                                onChange={this.setReportColumnsState}
                                onOpenChange={(open) => {
                                    if (!open) this.handleConfigChange()
                                }}
                                options={columnOptions}
                                defaultValue={defaultColumnOptions}
                                maxTagCount="responsive"
                                maxTagPlaceholder={(omittedValues) => `${omittedValues.length} selected`}
                                value={reportConfig.columns.length ? reportConfig.columns : undefined}
                            />
                        </Space>
                        <Space>
                            {!!currentSavedReport && (
                                <>
                                    {this.renderActionButton(
                                        currentSavedReport.isDefault ? (
                                            <StarFilled style={{ color: 'gold' }} />
                                        ) : (
                                            <StarOutlined style={{ color: 'gold' }} />
                                        ),
                                        'set-default-btn',
                                        this.handleSetDefaultReport,
                                        !actionsEnabled,
                                        currentSavedReport.isDefault ? 'Unmark as Default' : 'Mark as Default',
                                        undefined,
                                        currentSavedReport.isDefault ? 'gold' : undefined,
                                    )}
                                </>
                            )}
                            {this.state.platformLevel === 'domain' && (
                                <>
                                    {this.renderActionButton(
                                        <SaveOutlined />,
                                        'save-btn',
                                        () => this.setState({ savePanelVisible: true }),
                                        !actionsEnabled,
                                        'Save Report',
                                        (btn) => (
                                            <Popover
                                                title="Save Report"
                                                content={this.renderSaveReportPanel()}
                                                trigger="click"
                                                placement="bottomRight"
                                                getPopupContainer={this.appService.getAppContainer}
                                                // @todo - convert to classnames.root
                                                overlayClassName="save-report-overlay"
                                                open={this.state.savePanelVisible}
                                                onOpenChange={(savePanelVisible) =>
                                                    this.setState({
                                                        savePanelVisible,
                                                    })
                                                }
                                            >
                                                {btn}
                                            </Popover>
                                        ),
                                    )}

                                    <Divider orientation="vertical" size="small" />

                                    {this.renderActionButton(
                                        <ShareAltOutlined />,
                                        'share-btn',
                                        () => this.setState({ sharePanelVisible: true }),
                                        !actionsEnabled,
                                        'Share Report',
                                        (btn) => (
                                            <Popover
                                                title="Share Report"
                                                content={this.renderShareReportPanel()}
                                                trigger="click"
                                                placement="bottomRight"
                                                getPopupContainer={this.appService.getAppContainer}
                                                classNames={{
                                                    content: 'copy-report-overlay',
                                                }}
                                                onOpenChange={(sharePanelVisible) =>
                                                    this.setState({
                                                        sharePanelVisible,
                                                    })
                                                }
                                            >
                                                {btn}
                                            </Popover>
                                        ),
                                    )}
                                </>
                            )}

                            <div className="fx-col fx-shrink report-download-button">
                                {this.renderReportDownloadButton()}
                            </div>

                            <Divider orientation="vertical" size="small" />

                            <div className="fx-col fx-shrink">
                                <Tooltip title="Refresh">
                                    <AntDButton
                                        className="refresh-report"
                                        type="text"
                                        shape="round"
                                        icon={
                                            <ReloadOutlined className="text--size--large" spin={this.state.updating} />
                                        }
                                        onClick={this.handleRefresh}
                                    />
                                </Tooltip>
                            </div>
                        </Space>
                    </Flex>
                    {FILTERS_ENABLED && (
                        <Space align="center" className="report-builder-lower-ext">
                            <FilterBar
                                size="small"
                                filterDefinitions={this.getFilterDefinitions()}
                                value={fromReportFilters(filters, this.getFilterDefinitions())}
                                onChange={this.handleFilterBarChange}
                                onOpenChange={this.handleFilterBarOpenChange}
                                showInlineClearAll={false}
                                disabled={
                                    this.getFilterDefinitions().length === 0
                                        ? { tooltip: 'No filters currently available' }
                                        : false
                                }
                            />
                        </Space>
                    )}
                </Flex>
            </Flex>
        )
    }

    public get defaultReportTimeRange() {
        const defaultRangeConfig = TR_PRESET_LAST_7_DAYS.value
        return typeof defaultRangeConfig === 'function' ? defaultRangeConfig() : defaultRangeConfig
    }

    public renderActionButton(
        icon: any,
        className: string,
        onClick: () => any,
        disabled: boolean = false,
        tooltip?: React.ReactNode,
        renderPopover?: (children: React.ReactNode) => React.ReactNode,
        color?:
            | 'default'
            | 'danger'
            | 'primary'
            | 'blue'
            | 'purple'
            | 'cyan'
            | 'green'
            | 'magenta'
            | 'pink'
            | 'red'
            | 'orange'
            | 'yellow'
            | 'volcano'
            | 'geekblue'
            | 'lime'
            | 'gold',
    ): React.ReactNode {
        const button = (
            <AntDButton
                className={className}
                size="small"
                icon={icon}
                disabled={disabled}
                onClick={onClick}
                color={color}
            />
        )

        const anchor = (
            <span style={{ display: 'inline-flex' }} className="action-button-anchor">
                {button}
            </span>
        )

        const wrappedButton = !!renderPopover ? renderPopover(anchor) : anchor

        return !disabled && !!tooltip ? <Tooltip title={tooltip}>{wrappedButton}</Tooltip> : wrappedButton
    }

    public renderReportDownloadButton(): React.ReactNode {
        const actions = [
            {
                text: 'Download CSV Report',
                onClick: () => this.handleDownload('csv'),
            },
        ]

        const button = (
            <Button
                actions={actions}
                className="download-btn"
                size="small"
                icon={<CloudDownloadOutlined />}
                disabled={false}
                onClick={() => this.handleDownload('xlsx')}
            />
        )

        return (
            <Tooltip title="Download Excel Report">
                <span>{button}</span>
            </Tooltip>
        )
    }

    protected renderSaveReportPanel(): React.ReactNode {
        const { savedReports = [], currentSavedReport, savePanelVisible } = this.state

        let inputRef: any
        const isDefault = (currentSavedReport || {}).isDefault

        return (
            <>
                <Form>
                    <Space.Compact block>
                        <Space.Addon>Name: </Space.Addon>
                        <Input
                            key={`visible-${savePanelVisible}`}
                            ref={(el) => (inputRef = el)}
                            className="new-report-name-input"
                            defaultValue={(currentSavedReport || {}).name}
                            type="text"
                            maxLength={240}
                            autoFocus={true}
                        />
                    </Space.Compact>

                    <div className="align-right save-report-actions">
                        <span>
                            <AntDButton size="small" type="default" onClick={this.handleCloseSave}>
                                <span>Cancel</span>
                            </AntDButton>
                        </span>
                        <span>
                            <Button
                                size="small"
                                type="primary"
                                onClick={() =>
                                    this.handleSave((currentSavedReport || {}).id, inputRef.input.value, isDefault)
                                }
                                actions={
                                    !currentSavedReport
                                        ? undefined
                                        : [
                                              {
                                                  text: 'Save New',
                                                  onClick: () => this.handleSave(undefined, inputRef.input.value),
                                              },
                                          ]
                                }
                            >
                                <span>{!currentSavedReport ? 'Save' : 'Update'}</span>
                            </Button>
                        </span>
                    </div>
                </Form>
            </>
        )
    }

    protected get currentCtxEntity() {
        return this.state.platformLevel === PlatformLevel.DOMAIN ? this.state.currentDomain : this.state.currentOrg!
    }

    protected renderShareReportPanel(): React.ReactNode {
        return (
            <ShareReportPanel
                key={String(this.state.sharePanelVisible)}
                visible={this.state.sharePanelVisible}
                reportConfig={this.state.reportConfig}
                onClose={() => this.setState({ sharePanelVisible: false })}
            />
        )
    }

    protected getCurrentType(): ReportConfigurationType {
        const { reportConfig, savedReports } = this.state

        let type = reportConfig.type || ReportConfigurationType.NONE
        if (typeof type !== 'string') {
            type = savedReports.find((sr) => sr.id === type).reportConfig.type
        }

        return type
    }

    protected getCurrentLevel(): ReportConfigurationLevel {
        const { reportConfig } = this.state

        return reportConfig.level || 'NONE'
    }

    protected getCurrentBreakdown(): string {
        const { reportConfig } = this.state

        return reportConfig.breakdown || 'NONE'
    }

    protected determineTypeOptions(): Array<InsightsDropdownGroup<TypeDropdownOption>> {
        const { savedReports } = this.state
        let defaultOptions = clone(TYPE_DROPDOWN_OPTIONS)

        defaultOptions.forEach((opt) => {
            opt.options = opt.options.filter(
                (o) =>
                    o.condition?.({
                        platformLevel: this.state.platformLevel,
                        [this.state.platformLevel === PlatformLevel.ORG ? 'org' : 'domain']: this.currentCtxEntity,
                    }) ?? true,
            )
        })

        const typeOptions = [...defaultOptions]

        if (!!savedReports && savedReports.length > 0) {
            typeOptions.push({
                label: 'Saved',
                options: savedReports.map((sr) => ({
                    value: sr.id,
                    label: sr.name,
                })),
            })
        }

        return typeOptions
    }

    protected determineDefaultTypeValue(): ReportConfigurationType {
        const stub: any = {}
        const { reportConfig = stub } = this.state || stub

        return !reportConfig.type ? DEFAULT_TYPE.value : reportConfig.level || DEFAULT_TYPE.value
    }

    protected determineLevelOptions(type?: ReportConfigurationType): Array<LevelDropdownOption> {
        const { reportConfig } = this.state

        const _type = type || this.getCurrentType()

        const options: Array<LevelDropdownOption> = !reportConfig.type ? [] : LEVEL_DROPDOWN_OPTIONS[_type] || []

        return options.filter((opt) => {
            return (
                opt.condition?.({
                    type: _type,
                    platformLevel: this.state.platformLevel,
                    [this.state.platformLevel]: this.currentCtxEntity,
                }) ?? true
            )
        })
    }

    protected determineDefaultLevelValue(type?: ReportConfigurationType): ReportConfigurationLevel | undefined {
        const stub: any = {}
        const { reportConfig = stub } = this.state || stub

        return !(type || reportConfig.type) ? undefined : reportConfig.level || DEFAULT_LEVEL.value
    }

    protected determineBreakdownOptions(
        type?: ReportConfigurationType,
        level?: ReportConfigurationLevel,
    ): Array<BreakdownDropdownOption> {
        const { reportConfig } = this.state

        const _type = type ?? this.getCurrentType()
        const _level = level ?? this.getCurrentLevel()

        const options = !reportConfig.level ? [] : BREAKDOWN_DROPDOWN_OPTIONS[_type + '_' + _level] || []

        return options.filter((opt) => {
            return (
                opt.condition?.({
                    type: _type,
                    level: _level,
                    platformLevel: this.state.platformLevel,
                    [this.state.platformLevel]: this.currentCtxEntity,
                }) ?? true
            )
        })
    }

    protected determineDefaultBreakdownValue(
        level?: ReportConfigurationLevel,
    ): ReportConfigurationBreakdown | undefined {
        const stub: any = {}
        const { reportConfig = stub } = this.state || stub

        return !(level || reportConfig.level) ? undefined : reportConfig.breakdown || DEFAULT_BREAKDOWN.value
    }

    protected determineColumnOptions(
        type?: ReportConfigurationType,
        level?: ReportConfigurationLevel,
        breakdown?: ReportConfigurationBreakdown,
    ): Array<InsightsDropdownGroup<ColumnDropdownOption>> {
        const { reportConfig } = this.state

        const _type = type ?? this.getCurrentType()
        const _level = level ?? this.getCurrentLevel()
        const _breakdown = breakdown ?? this.getCurrentBreakdown()

        // A/B Test + Notification is a UX alias for "notification rows scoped to test".
        // Reuse notification column catalog so users get notification dimensions/measures.
        const effectiveLevel = resolveEffectiveLevelForCatalog(_type, _level, _breakdown)
        const optionKey = `${_type}_${effectiveLevel}`

        const optionCategories: Array<InsightsDropdownGroup<ColumnDropdownOption>> = !reportConfig.level
            ? []
            : COLUMN_DROPDOWN_OPTIONS[optionKey] || []

        return optionCategories
            .map((cat) => {
                const filteredOptions = cat.options.filter((opt) => {
                    return (
                        opt.condition?.({
                            type: _type,
                            level: _level,
                            breakdown: _breakdown,
                            platformLevel: this.state.platformLevel,
                            [this.state.platformLevel]: this.currentCtxEntity,
                        } as EntityConditionOptions) ?? true
                    )
                })

                const sortedOptions = filteredOptions.sort((a, b) => {
                    const aLabel = (a.label || a.value).toLowerCase()
                    const bLabel = (b.label || b.value).toLowerCase()
                    return aLabel > bLabel ? 1 : aLabel < bLabel ? -1 : 0
                })

                return {
                    ...cat,
                    // Keep defaults as-is, but present each group in predictable alpha order.
                    options: sortedOptions,
                }
            })
            .filter((cat) => cat.options.length > 0)
    }

    protected determineDefaultColumnValues(
        columnOptions: Array<InsightsDropdownGroup<ColumnDropdownOption>>,
    ): string[] {
        const dims: ColumnDropdownOption[] =
            (columnOptions.find((opt) => opt.label.toUpperCase() === 'DIMENSIONS') || ({} as any)).options || []
        const measures: ColumnDropdownOption[] =
            (columnOptions.find((opt) => opt.label.toUpperCase() === 'MEASURES') || ({} as any)).options || []

        return [...dims.filter((opt) => opt.default === true), ...measures.filter((opt) => opt.default === true)].map(
            (opt) => opt.value,
        )
    }

    protected determineFilterOptions(
        type?: ReportConfigurationType,
        level?: string,
        breakdown?: string,
        columns?: string[],
    ): FiltersDropdownOption[] {
        const { reportConfig } = this.state

        type = type || this.getCurrentType()
        level = level || this.getCurrentLevel()
        breakdown = breakdown || this.getCurrentBreakdown()
        columns = (columns || this.state.reportConfig.columns || []).map((c) => c.replace('.', '_'))

        const filterSelector = isDeliveryTestNotificationAlias(type, level as ReportConfigurationLevel, breakdown)
            ? `${ReportConfigurationType.DELIVERY}_${ReportConfigurationLevel.NOTIFICATION}_${ReportConfigurationBreakdown.NONE}`
            : type +
              '_' +
              (level || reportConfig.level || 'NONE') +
              '_' +
              (breakdown || reportConfig.breakdown || 'NONE')

        const options = !reportConfig.level ? [] : FILTER_DROPDOWN_OPTIONS[filterSelector] || []

        const filteredOptions = options.filter(
            (opt) =>
                ((opt.condition?.({
                    type,
                    level,
                    breakdown,
                    platformLevel: this.state.platformLevel,
                    [this.state.platformLevel]: this.currentCtxEntity,
                } as EntityConditionOptions) ??
                    true) &&
                    !opt.columnRequired) ||
                columns!.includes(opt.value),
        )

        filteredOptions.sort((a, b) => {
            const aName = (a.label || a.value).toLowerCase()
            const bName = (b.label || b.value).toLowerCase()
            return aName > bName ? 1 : aName < bName ? -1 : 0
        })

        return filteredOptions
    }

    @autobind
    protected async setDomainState(): Promise<void> {
        const currentDomain = this.currentCtxEntity || ({} as any)
        const globalDomain = this.globalDomain

        if (globalDomain) {
            if (currentDomain.id !== globalDomain.id) {
                await this.setState(({ reportConfig, ...rest }) => ({
                    ...rest,
                    platformLevel: PlatformLevel.DOMAIN,
                    currentDomain: globalDomain,
                    currentSavedReport: undefined,
                    showFilters: false,
                    reportConfig: {
                        ...reportConfig,
                        filters: undefined,
                    },
                }))
            }
        }

        await this.fetchSavedReports()

        return this.handleConfigChange(true, 'LOAD_SAVED_REPORT')
    }

    protected async fetchSavedReports(): Promise<void> {
        if (this.state.platformLevel !== PlatformLevel.DOMAIN || !this.state.currentDomain) return

        if (this.state.currentDomain) {
            this.appService.setModuleLoading()

            const savedReports = await this.insightsService.getSavedReports(
                this.state.currentDomain.id,
                this.appState.currentUser!.id,
                false,
                'fetchSavedReports',
            )

            const defaultReport = (savedReports || []).find((sr: any) => sr.isDefault)

            let requestedReport: any
            if (this.queryString.report_id) {
                const shared = this.queryString.shared?.toString().trim() !== '0'

                try {
                    requestedReport = await this.insightsService.loadReportFromLink(
                        this.state.currentDomain.id,
                        this.queryString.report_id as any,
                        shared,
                        false,
                        'fetchSharedReport',
                    )
                } catch (err) {}
            }

            await this.setState(({ reportConfig }) => {
                const newState: any = {
                    savedReports: this.sortSavedReports(savedReports),
                    reportConfig,
                }

                if (requestedReport) {
                    newState.reportConfig = requestedReport.reportConfig
                    newState.currentSavedReport = requestedReport
                } else if (defaultReport) {
                    if (defaultReport.reportConfig.dateRangePresetKey) {
                        defaultReport.reportConfig.dateRange = this.getRangeFromPresetKey(
                            defaultReport.reportConfig.dateRangePresetKey,
                        )
                    }

                    newState.currentSavedReport = defaultReport
                    newState.reportConfig = defaultReport.reportConfig
                }

                return newState
            })

            if (defaultReport || requestedReport) {
                const loadedReport = requestedReport || defaultReport

                if (loadedReport.reportConfig.tableSort) {
                    this.triggerForcedReportSort(loadedReport.reportConfig.tableSort)
                }
            }

            this.appService.unsetModuleLoading()
        }
    }

    protected getRangeFromPresetKey(presetKey: TimeRangePresetKey): { since: string; through: string } {
        const defaultRange = TIME_RANGE_PRESETS.find((preset) => preset.key === presetKey)!
        const range = typeof defaultRange.value === 'function' ? defaultRange.value() : defaultRange.value

        return {
            since: range[0]?.format('YYYY-MM-DD') ?? undefined!,
            through: range[1]?.format('YYYY-MM-DD') ?? undefined!,
        }
    }

    protected triggerForcedReportSort(detail: any): void {
        window.__SW_REPORT_TABLE__ = {
            ...(window.__SW_REPORT_TABLE__ ?? {}),
            ...detail,
        }

        window.dispatchEvent(new CustomEvent('sw-force-report-sort', { detail }))
    }

    protected sortSavedReports(reports: any[]): any[] {
        return reports.sort((a: any, b: any) => {
            return a.name > b.name ? 1 : a.name < b.name ? -1 : 0
        })
    }

    @autobind
    protected async setShowFiltersState(): Promise<void> {
        await this.setState(({ showFilters, reportConfig }) => {
            const newState: any = {
                showFilters: !showFilters,
            }

            if (!newState.showFilters) {
                newState.reportConfig = {
                    ...reportConfig,
                    filters: undefined,
                }
            }

            return newState
        })

        this.handleConfigChange()
    }

    @autobind
    protected async setDateIncrementState(dateIncrement: ReportDateIncrement): Promise<void> {
        await this.setState(({ reportConfig }) => ({
            reportConfig: {
                ...reportConfig,
                dateIncrement,
            },
        }))

        this.handleConfigChange()
    }

    @autobind
    protected async setDateRangeState(values: RangeValueWithPreset<Dayjs> | any): Promise<void> {
        const dateRangePresetKey = values?.length === 3 ? values[2] : undefined

        const dateRange: any = {}
        dateRange.since = !values?.[0] ? undefined : values[0].format('YYYY-MM-DD')
        dateRange.through = !values?.[1] ? undefined : values[1].format('YYYY-MM-DD')

        await this.setState(({ reportConfig }) => ({
            reportConfig: {
                ...reportConfig,
                dateRange,
                dateRangePresetKey,
            },
        }))

        this.handleConfigChange()
    }

    @autobind
    protected async setReportTypeState(type: ReportConfigurationType): Promise<void> {
        if (typeof type !== 'string') {
            await this.setCurrentSavedReportState(type)
        } else {
            await this.setState(({ reportConfig, updateKey, updating }) => {
                const useSavedReportValues = updateKey === 'LOAD_SAVED_REPORT' && updating
                const columnOptions = this.determineColumnOptions(type, DEFAULT_LEVEL.value)
                const columnSelections = this.determineDefaultColumnValues(columnOptions)

                if (type === 'MONTHLY_UNIQUES') {
                    this.setDateRangeState(TR_PRESET_ALL_TIME.value)
                } else if (reportConfig.type === 'MONTHLY_UNIQUES') {
                    this.setDateRangeState(TR_PRESET_LAST_7_DAYS.value)
                }

                return {
                    currentSavedReport: undefined,
                    reportConfig: {
                        type,
                        level: useSavedReportValues ? reportConfig.level : DEFAULT_LEVEL.value,
                        dateRange: reportConfig.dateRange,
                        dateIncrement:
                            type === 'DELIVERY'
                                ? reportConfig.dateIncrement === 'LIFETIME'
                                    ? ReportDateIncrement.YEAR
                                    : reportConfig.dateIncrement
                                : type === 'MONTHLY_UNIQUES'
                                ? ReportDateIncrement.MONTH
                                : reportConfig.dateIncrement,
                        breakdown: useSavedReportValues ? reportConfig.breakdown : DEFAULT_BREAKDOWN.value,
                        columns: useSavedReportValues ? reportConfig.columns : columnSelections,
                        filters: useSavedReportValues ? reportConfig.filters : undefined,
                    },
                }
            })
        }

        this.handleConfigChange()
    }

    @autobind
    protected async setReportLevelState(level: ReportConfigurationLevel): Promise<void> {
        if (level !== this.state.reportConfig.level) {
            await this.setState(({ reportConfig, updateKey, updating }) => {
                const useSavedReportValues = updateKey === 'LOAD_SAVED_REPORT' && updating

                const columnOptions = this.determineColumnOptions(reportConfig.type, level)
                const defaultColumnOptions = this.determineDefaultColumnValues(columnOptions)

                return {
                    reportConfig: {
                        ...reportConfig,
                        level,
                        breakdown: useSavedReportValues ? reportConfig.breakdown : DEFAULT_BREAKDOWN.value,
                        columns: useSavedReportValues ? reportConfig.columns : defaultColumnOptions,
                        filters: useSavedReportValues ? reportConfig.filters : undefined,
                    },
                }
            })
        }

        this.handleConfigChange()
    }

    @autobind
    protected async setReportBreakdownState(breakdown: ReportConfigurationBreakdown): Promise<void> {
        if (breakdown !== this.state.reportConfig.breakdown) {
            await this.setState(({ reportConfig, updateKey, updating }) => {
                const useSavedReportValues = updateKey === 'LOAD_SAVED_REPORT' && updating
                const availableFilters = this.determineFilterOptions(undefined, undefined, breakdown)
                const availableFilterFields = availableFilters.map((f) => f.value)
                const filters = reportConfig.filters || []
                const allowedFilters = useSavedReportValues
                    ? filters
                    : filters.filter((f) => availableFilterFields.includes(f.field))

                const config = {
                    ...reportConfig,
                    breakdown,
                    columns: reportConfig.columns || [],
                    filters: allowedFilters.length === 0 ? undefined : allowedFilters,
                }

                const isDeliveryTestNotificationView = isDeliveryTestNotificationAlias(
                    reportConfig.type,
                    reportConfig.level,
                    breakdown,
                )

                if (breakdown === 'BUTTON') {
                    config.columns.push('BUTTON.TYPE')
                    config.columns.push('BUTTON.LABEL')
                } else {
                    config.columns = config.columns.filter((col) => !/^button/i.test(col))
                }

                if (breakdown === ReportConfigurationBreakdown.VARIANT) {
                    if (!config.columns.includes('SPLIT_TEST.NAME')) config.columns.push('SPLIT_TEST.NAME')
                    if (!config.columns.includes('VARIANT.NAME')) config.columns.push('VARIANT.NAME')
                    if (!config.columns.includes('VARIANT.PERCENTAGE')) config.columns.push('VARIANT.PERCENTAGE')
                } else if (breakdown === ReportConfigurationBreakdown.SPLIT_TEST) {
                    if (!config.columns.includes('SPLIT_TEST.NAME')) config.columns.push('SPLIT_TEST.NAME')
                } else if (breakdown === ReportConfigurationBreakdown.STEP) {
                    if (!config.columns.includes('STEP.NAME')) config.columns.push('STEP.NAME')
                    if (!config.columns.includes('STEP.TYPE')) config.columns.push('STEP.TYPE')
                } else if (breakdown === ReportConfigurationBreakdown.CHANNEL) {
                    if (!config.columns.includes('CHANNEL')) config.columns.push('CHANNEL')
                }

                if (isDeliveryTestNotificationView && !useSavedReportValues) {
                    // UX alias:
                    // "Level = A/B Test" + "Breakdown = Notification" should behave like
                    // notification-level reporting scoped to tests. Seed notification defaults
                    // and keep test identity columns visible.
                    const defaultNotificationColumns = this.determineDefaultColumnValues(
                        this.determineColumnOptions(
                            reportConfig.type,
                            reportConfig.level,
                            ReportConfigurationBreakdown.NOTIFICATION,
                        ),
                    )
                    config.columns = Array.from(new Set([...defaultNotificationColumns, 'TEST.ID', 'TEST.NAME']))
                }

                if (this.state.platformLevel === PlatformLevel.ORG) {
                    if (breakdown.includes(ReportConfigurationBreakdown.DOMAIN)) {
                        config.columns.push('DOMAIN.ID')
                        config.columns.push('DOMAIN.DISPLAY_NAME')
                        if (reportConfig.type === ReportConfigurationType.DELIVERY) {
                            config.columns.push('NOTIFICATION_GROUP.ID', 'NOTIFICATION.ID')
                        }
                    } else {
                        config.columns = config.columns.filter((col) => !/^domain/i.test(col))
                    }
                }

                // Prune columns whose breakdown conditions no longer hold
                // This prevents phantom selections when switching between
                // breakdowns that have conditional columns (e.g., Variant → Step)
                const availableColumnOptions = this.determineColumnOptions(
                    reportConfig.type,
                    reportConfig.level,
                    breakdown,
                )
                const availableColumnValues = new Set(
                    availableColumnOptions.flatMap((group) => group.options.map((opt) => opt.value)),
                )
                config.columns = config.columns.filter((col) => availableColumnValues.has(col))

                return {
                    reportConfig: config,
                }
            })
        }

        this.handleConfigChange()
    }

    @autobind
    protected async setReportColumnsState(columns: string[]): Promise<void> {
        await this.setState(({ reportConfig }) => {
            return {
                reportConfig: {
                    ...reportConfig,
                    columns,
                },
            }
        })
    }

    // FILTERSv2
    protected handleFilterAdd = async () => {
        return this.setState(({ reportConfig }) => ({
            reportConfig: {
                ...reportConfig,
                filters: [
                    ...(reportConfig.filters || []),
                    {
                        $mode: 'new',
                    } as any,
                ],
            },
        }))
    }

    protected handleFilterUpdate = async (update, filterIdx) => {
        if (filterIdx !== -1) {
            await this.setState(({ reportConfig }) => {
                const filtersCopy = clone(reportConfig.filters || [])
                filtersCopy.splice(filterIdx, 1, update)

                return {
                    reportConfig: {
                        ...reportConfig,
                        filters: filtersCopy,
                    },
                }
            })

            if (!!update.value || VALUELESS_OPERATORS.includes(update.operator)) {
                this.handleConfigChange()
            }
        }
    }

    protected handleFilterRemove = async (filterIdx) => {
        await this.setState(({ reportConfig }) => {
            const filtersCopy = clone(reportConfig.filters || [])
            filtersCopy.splice(filterIdx, 1)

            return {
                reportConfig: {
                    ...reportConfig,
                    filters: filtersCopy,
                },
            }
        })

        this.handleConfigChange()
    }
    //

    /**
     * Returns FilterDefinition[] for the current report context.
     * Cached per render — rebuilt when type/level/breakdown changes.
     */
    protected getFilterDefinitions(): FilterDefinition[] {
        return buildReportingFilterDefinitions(
            this.getCurrentType(),
            this.getCurrentLevel(),
            this.getCurrentBreakdown() as ReportConfigurationBreakdown,
            this.state.platformLevel,
            this.currentCtxEntity,
        )
    }

    /**
     * Bridges FilterBar onChange (ActiveFilter[]) to reportConfig state.
     * When the popover is open, we defer the refresh to handleFilterBarOpenChange
     * so all selections are batched into one request. When the popover is closed
     * (inline tag removal, clear all), we refresh immediately.
     */
    /**
     * Bridges FilterBar onChange (ActiveFilter[]) to reportConfig state.
     * When the popover is open, we defer the refresh to handleFilterBarOpenChange
     * so all selections are batched into one request. When the popover is closed
     * (inline tag removal, clear all), we refresh immediately.
     *
     * handleConfigChange is called outside the setState callback because React 18
     * batching can delay or merge callbacks when multiple setStates fire in the
     * same tick (e.g. onOpenChange + onChange from a tag × click). Since
     * handleConfigChange uses a 50ms debounced setTimeout that reads
     * this.state.reportConfig, the state will be flushed by the time it fires.
     */
    protected handleFilterBarChange = (activeFilters: ActiveFilter[]) => {
        const reportFilters = activeFilters.length > 0 ? toReportFilters(activeFilters) : undefined
        this.setState(({ reportConfig }) => {
            const state: Partial<ReportBuilderState> = {
                reportConfig: {
                    ...reportConfig,
                    filters: reportFilters as any,
                },
            }

            if (!reportFilters) {
                state.showFilters = false
            }

            return state as ReportBuilderState
        })

        if (!this._filterPopoverOpen) {
            this.handleConfigChange()
        }
    }

    /**
     * Called when the FilterBar popover opens or closes.
     * Triggers an API refresh on close so we batch all filter changes.
     */
    private _filterPopoverOpen = false
    protected handleFilterBarOpenChange = (open: boolean) => {
        this._filterPopoverOpen = open
        if (!open) {
            this.handleConfigChange()
        }
    }

    @autobind
    protected async setReportFiltersState(filters?: any[]): Promise<void> {
        await this.setState(({ reportConfig }) => {
            const state: Partial<ReportBuilderState> = {
                reportConfig: {
                    ...reportConfig,
                    filters,
                },
            }

            if (!filters) {
                state.showFilters = false
            }

            return state as ReportBuilderState
        })

        this.handleConfigChange()
    }

    @autobind
    protected async setReportQueryState(query: string): Promise<void> {
        await this.setState(({ reportConfig }) => ({
            reportConfig: {
                ...reportConfig,
                query,
            },
        }))

        this.handleConfigChange()
    }

    @autobind
    protected async setCurrentSavedReportState(reportId: number): Promise<void> {
        const { savedReports = [] } = this.state
        const report = savedReports.find((r: any) => r.id === reportId)

        if (report) {
            await this.setState(({ reportConfig }) => ({
                currentSavedReport: report,
                reportConfig: {
                    ...reportConfig,
                    ...report.reportConfig,
                },
                showFilters: (report.reportConfig.filters || []).length > 0,
            }))

            if (!!report.reportConfig.tableSort) {
                this.triggerForcedReportSort(report.reportConfig.tableSort)
            }

            return this.handleConfigChange(true, 'LOAD_SAVED_REPORT')
        }
    }

    protected getDefaultReportDateRange(): any {
        const range = this.defaultReportTimeRange

        return {
            since: range[0]?.format('YYYY-MM-DD') ?? null,
            through: range[1]?.format('YYYY-MM-DD') ?? null,
        }
    }

    @autobind
    protected async handleDownload(format: 'csv' | 'xlsx'): Promise<void> {
        const { reportConfig, currentSavedReport } = this.state
        const type = reportConfig.type!.toLowerCase()
        const level = reportConfig.level!.toLowerCase()
        const breakdown = reportConfig
            .breakdown!.toLowerCase()
            .split(',')
            .map((bd) => (bd === 'placement' ? 'device-type' : bd))
            .filter((bd) => bd !== 'none')
            .join('-')

        const contractOptions: ReportContractOptions = {
            downloadable: true,
        }
        if (this.state.platformLevel === PlatformLevel.DOMAIN) {
            contractOptions.domain_ids = [this.currentCtxEntity.id]
        } else {
            contractOptions.organization_id = this.currentCtxEntity.id
        }

        const contract = buildApiReportContract(
            reportConfig,
            this.getCurrentType(),
            this.getCurrentLevel(),
            contractOptions,
        )

        contract.format = format
        contract.filename = !!currentSavedReport
            ? snakeCase(currentSavedReport.name)
            : `${type}_${level}${!!breakdown ? '_' + breakdown : ''}`

        if (!currentSavedReport && !!reportConfig.dateRange && !!reportConfig.dateRange.since) {
            const range: any = reportConfig.dateRange
            const since = range.since.replace(/-/gi, '')
            const through = range.through.replace(/-/gi, '')

            contract.filename = `${contract.filename}_${since}-${through}`
        }

        contract.filename = contract.filename + '_report'

        // CSV download exclusions
        const exclusions = ['domain.id', 'domain.name', 'group.id', 'group.name', 'notification.source']
        exclusions.forEach((exclusion) => {
            const exclusionIndex = contract.fields.findIndex((f: string) => f === exclusion)
            if (exclusionIndex !== -1) {
                contract.fields.splice(exclusionIndex, 1)
            }
        })

        try {
            this.fetchInsights(contract, true)
        } catch (err) {
            this.sendErrorStatus(err)
        }
    }

    @autobind
    protected async handleRefresh(): Promise<void> {
        await this.setState({ updating: true })

        await this.updateData(undefined, true)

        await this.setState({ updating: false })
    }

    @autobind
    protected async handleCloseSave(...args: any[]): Promise<void> {
        return this.setState({ savePanelVisible: false })
    }

    @autobind
    protected async handleSave(
        reportId: number | undefined,
        reportName: string,
        isDefault: boolean = false,
    ): Promise<void> {
        const reportConfig = clone(this.state.reportConfig)
        const globalTable = (window as any).__SW_REPORT_TABLE__ || {}

        // XOR preset or range
        if (!!reportConfig.dateRangePresetKey) {
            delete reportConfig.dateRange
        } else {
            delete reportConfig.dateRangePresetKey
        }

        reportConfig.tableSort = {
            sortBy: globalTable.sortBy,
            sortDirection: globalTable.sortDirection,
        }

        const trimmedReportName = reportName.toString().trim()
        if (!reportName || !trimmedReportName) {
            simpleNotification('error', 'Report name cannot be empty.', undefined, 'saveUpdateReport')
            return
        }

        reportConfig.filters = this.parseExecutableFilters(reportConfig.filters)

        const savedReport = await this.insightsService.save(
            this.appState.currentDomain!.id,
            this.appState.currentUser!.id,
            reportId,
            trimmedReportName,
            isDefault,
            reportConfig,
        )

        if (savedReport) {
            simpleNotification(
                'success',
                reportId
                    ? `${trimmedReportName} has been successfully updated.`
                    : `${trimmedReportName} has been successfully saved.`,
                undefined,
                'saveUpdateReport',
            )

            await this.setState(({ savedReports, currentSavedReport }) => {
                const update = {
                    currentSavedReport: savedReport,
                    savedReports,
                }

                if (currentSavedReport && savedReport.id === currentSavedReport.id) {
                    const currentReportIndex = savedReports.findIndex((r: any) => r.id === reportId)
                    if (currentReportIndex !== -1) {
                        update.savedReports.splice(currentReportIndex, 1, savedReport)
                    }
                } else {
                    update.savedReports = [...update.savedReports, savedReport]

                    update.savedReports = this.sortSavedReports(update.savedReports)
                }

                return update
            })

            this.handleCloseSave()
        }
    }

    protected parseExecutableFilters(filters: any[] | undefined): ReportFilter[] {
        if (!filters) {
            return []
        }
        const executable = clone(filters)

        return executable
            .map((f) => {
                delete f.$mode
                return f
            })
            .filter(
                (f) =>
                    !!f.field &&
                    !!f.operator &&
                    (VALUELESS_OPERATORS.includes(f.operator) ||
                        (f.value !== null && f.value !== undefined && f.value.toString().trim() !== '')),
            )
    }

    @autobind
    protected async handleSetDefaultReport(): Promise<void> {
        const { reportConfig, currentSavedReport: csr } = this.state

        const savedReport = await this.insightsService.save(
            this.appState.currentDomain!.id,
            this.appState.currentUser!.id,
            csr.id,
            csr.name,
            !csr.isDefault,
            reportConfig,
            true,
        )

        if (!!savedReport) {
            simpleNotification(
                'success',
                savedReport.isDefault
                    ? `${savedReport.name} has been set as your default report.`
                    : `${savedReport.name} has been unset as your default report.`,
                undefined,
                'setDefaultReport',
            )

            this.setState(({ savedReports }) => {
                const update = {
                    currentSavedReport: savedReport,
                    savedReports,
                }

                const lastDefaultIndex = savedReports.findIndex((r: any) => r.isDefault)
                if (lastDefaultIndex !== -1) {
                    const lastDefault = savedReports.find((r: any) => r.isDefault)
                    lastDefault.isDefault = false
                    update.savedReports.splice(lastDefaultIndex, 1, lastDefault)
                }

                const currentReportIndex = savedReports.findIndex((r: any) => r.id === csr.id)
                if (currentReportIndex !== -1) {
                    update.savedReports.splice(currentReportIndex, 1, savedReport)
                }

                update.savedReports = this.sortSavedReports(update.savedReports)

                return update
            })
        }
    }

    @autobind
    protected async handleConfigChange(
        fullPathUpdate: boolean = false,
        updateKey: string = 'updateKey',
    ): Promise<void> {
        if (UPDATE_DEBOUNCE) {
            clearTimeout(UPDATE_DEBOUNCE)
        }

        if (!this.state.updating) {
            this.setState({ updateKey, updating: true })
        }

        UPDATE_DEBOUNCE = setTimeout(async () => {
            const { reportConfig } = this.state

            const canProcessConfig =
                !!reportConfig.type && !!reportConfig.level && !!reportConfig.breakdown && !!reportConfig.columns

            if (canProcessConfig) {
                this.updatePathConfig(reportConfig, fullPathUpdate)

                if (!this.state.showFilters && (reportConfig.filters || []).length > 0) {
                    this.setState({ showFilters: true })
                }

                await this.updateData(reportConfig)
            } else {
                if (!reportConfig.type) {
                    this.sendSelectTypeStatus()
                } else if (!reportConfig.level) {
                    this.sendSelectLevelStatus()
                }
            }

            this.setState({ updating: false })
        }, 50)
    }

    protected sendErrorStatus(err: Error): void {
        this.props.onStatusUpdate({
            code: ReportStatusCode.ERROR,
            message: (
                <span>
                    An error has occured while generating your report.
                    <br />
                    Please contact your account manager for assistance.
                </span>
            ),
        })
    }

    protected sendSelectTypeStatus(): void {
        this.props.onStatusUpdate({
            code: ReportStatusCode.NOT_READY,
            message: (
                <span>
                    Please select your desired report <b>Type</b> to get started.
                </span>
            ),
        })
    }

    protected sendSelectLevelStatus(): void {
        this.props.onStatusUpdate({
            code: ReportStatusCode.NOT_READY,
            message: (
                <span>
                    Please select your desired report <b>Level</b> to continue.
                </span>
            ),
        })
    }

    protected sendLoadingStatus(): void {
        this.props.onStatusUpdate({
            code: ReportStatusCode.LOADING,
        })
    }

    protected sendReadyStatus(data: any[]): void {
        const hasData = data.length > 0
        this.props.onStatusUpdate({
            code: hasData ? ReportStatusCode.READY : ReportStatusCode.NO_DATA,
            message: hasData ? void 0 : <span>No data found for the specified report configuration.</span>,
        })
    }

    protected async updateData(config?: any, forceUpdate: boolean = false): Promise<void> {
        this.sendLoadingStatus()
        config = config || this.state.reportConfig

        const contractOptions: ReportContractOptions = {}
        if (this.state.platformLevel === PlatformLevel.DOMAIN) {
            contractOptions.domain_ids = [this.currentCtxEntity.id]
        } else {
            contractOptions.organization_id = this.currentCtxEntity.id
        }

        const contract = buildApiReportContract(config, this.getCurrentType(), this.getCurrentLevel(), contractOptions)

        const contractSig = btoa(unescape(encodeURIComponent(JSON.stringify(contract))))

        if (forceUpdate || contractSig !== this.state.lastCallSignature) {
            await this.setState({ lastCallSignature: contractSig })

            try {
                contract.filters = this.parseExecutableFilters(contract.filters)

                const data = await this.fetchInsights(contract)
                this.props.onInsightsUpdate(data, config, contract)

                this.sendReadyStatus(data)
            } catch (err) {
                this.sendErrorStatus(err)
            }
        }
    }

    protected async fetchInsights(contract: InsightsReportContract, showLoadingScreen: boolean = true): Promise<any> {
        const cleanContract = clone(contract)
        delete cleanContract.addtRequiredFields

        return this.insightsService.fetch(cleanContract, {
            showLoadingScreen: true,
            requestScope: this.state.platformLevel === PlatformLevel.ORG ? 'organizations' : 'domains',
            scopeId: this.currentCtxEntity.id,
            cancellationKey: 'insights-request',
        })
    }

    protected async readReportFromQueryString(): Promise<void> {
        const { reportConfig: stateConfig } = this.state
        const allowedKeys = ['type', 'level', 'breakdown', 'dateRange', 'dateIncrement', 'columns', 'filters']

        const QUERY_UPDATES_ENABLED = false
        if (!QUERY_UPDATES_ENABLED) {
            return
        }

        if (!!this.queryString) {
            if (!this.queryString.report) {
                this.sendSelectTypeStatus()

                if (!!stateConfig.type && !!stateConfig.level && !!stateConfig.breakdown) {
                    const type = this.determineDefaultTypeValue()
                    const level = this.determineDefaultLevelValue(type)
                    const breakdown = this.determineDefaultBreakdownValue(level)
                    const columns = this.determineDefaultColumnValues(
                        this.determineColumnOptions(type, level, breakdown),
                    )

                    // RESET FORM
                    await this.setState({
                        reportConfig: {
                            type,
                            level: level ?? ReportConfigurationLevel.DOMAIN,
                            breakdown: breakdown ?? ReportConfigurationBreakdown.NONE,
                            columns,
                            dateIncrement: ReportDateIncrement.DAY,
                            dateRange: this.getDefaultReportDateRange(),
                        },
                    })
                }
            } else {
                const currentConfig: any = {}
                const requestedConfig: any = {}

                try {
                    const parsedConfigReq = JSON.parse(this.queryString.report as any)
                    const reqKeys = Object.keys(parsedConfigReq).map((k) => camelCase(k))

                    reqKeys.forEach((reqKey) => {
                        if (allowedKeys.indexOf(reqKey) !== -1) {
                            currentConfig[reqKey] = stateConfig[reqKey]
                            requestedConfig[reqKey] = parsedConfigReq[reqKey]
                        }
                    })

                    const currentSignature = btoa(unescape(encodeURIComponent(JSON.stringify(currentConfig))))
                    const requestedSignature = btoa(unescape(encodeURIComponent(JSON.stringify(requestedConfig))))

                    if (currentSignature !== requestedSignature) {
                        await this.setState(({ reportConfig }) => ({
                            reportConfig: {
                                ...reportConfig,
                                ...requestedConfig,
                            },
                        }))

                        this.handleConfigChange()
                    }
                } catch {}
            }
        } else {
            this.sendSelectTypeStatus()
        }
    }

    protected updatePathConfig(config: IReportConfig, fullPathUpdate: boolean = false): void {
        // tslint:disable
        let { columns, ...parsedConfig } = config
        let updatedConfig: IReportConfig = {
            ...parsedConfig,
            columns: [],
        }
        // tslint:enable

        if (fullPathUpdate) {
            updatedConfig = {
                ...parsedConfig,
                columns,
            }
        }

        const currentConfigSig = btoa(unescape(encodeURIComponent(this.queryString.report as any)))
        const newConfigSig = btoa(unescape(encodeURIComponent(JSON.stringify(updatedConfig))))

        if (currentConfigSig !== newConfigSig) {
            const parsedUpdate = `?report=${JSON.stringify(updatedConfig)}`
            // this.injectedProps.history.push({ search: parsedUpdate });
        }
    }
}
